import axios from 'axios';
import type { FrameCalibrationStatus } from '@designdeck/shared';
import { getErrorMessage } from '@/api';
import type { CalibrateTemplateResult } from '@/api/admin';
import { StatusPill } from '@/components/ui';
import { MM_FIELD_LABEL } from './printAreaDraft';

/**
 * What the admin pages show around a template calibration: the status of
 * a template, the message of a refused save and of a stored one, and the
 * banner when the product is not selectable. The product page and the
 * frame-review walkthrough calibrate through the same endpoint (R10), so
 * they say the same things.
 */

const STATUS_PILL: Record<
  FrameCalibrationStatus,
  { label: string; variant: 'published' | 'warning' | 'danger'; title: string }
> = {
  calibrated: {
    label: 'kalibriert',
    variant: 'published',
    title: 'Rahmen dieser Vorlage ist gegen die mm der Fläche kalibriert.',
  },
  inconsistent: {
    label: 'prüfen',
    variant: 'warning',
    title:
      'Gespeicherter Entwurf: prüfen und übernehmen oder neu ziehen. Bis dahin erscheint die Vorlage nicht im Creator.',
  },
  uncalibrated: {
    label: 'unkalibriert',
    variant: 'danger',
    title:
      'Keine Kalibrierung (oder keine mm an der Fläche). Bis zum Speichern erscheint die Vorlage nicht im Creator.',
  },
};

export function CalibrationStatusPill({
  status,
  className,
}: {
  status: FrameCalibrationStatus;
  className?: string;
}) {
  const pill = STATUS_PILL[status];
  return (
    <StatusPill
      variant={pill.variant}
      title={pill.title}
      className={className}
      data-testid="calibration-status"
    >
      {pill.label}
    </StatusPill>
  );
}

/**
 * The message of a refused calibration. A 422 names the millimetre field
 * the print area lacks (R9); everything else is the server's message.
 */
export function calibrationErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 422) {
    const data: unknown = error.response.data;
    const field =
      typeof data === 'object' && data !== null && 'params' in data
        ? (data.params as { field?: unknown } | null)?.field
        : undefined;
    if (typeof field === 'string') {
      const label =
        field in MM_FIELD_LABEL
          ? MM_FIELD_LABEL[field as keyof typeof MM_FIELD_LABEL]
          : field;
      return `Kalibrieren nicht möglich: ${label} fehlt an der Fläche.`;
    }
  }
  return getErrorMessage(error);
}

/** The success hint of a calibration: its mockups are rendered again. */
export function calibrationSavedMessage(
  result: Pick<CalibrateTemplateResult, 'mockups'>
): string {
  const queued = result.mockups?.queued;
  return queued != null
    ? `Kalibriert · Mockups werden neu gerendert (${queued})`
    : 'Kalibriert · Mockups werden neu gerendert';
}

const NOT_SELECTABLE_TEXT =
  'Das Produkt ist für Seller und Direct Sale nicht wählbar: die Standard-Position hat kein kalibriertes Default-Template oder keine mm-Maße.';

/** Banner after a save whose answer says the product is not selectable. */
export function NotSelectableBanner({ warning }: { warning: string | null }) {
  return (
    <p
      role="alert"
      className="rounded-control bg-warning px-3 py-2 text-xs text-ink"
      data-testid="not-selectable-banner"
    >
      {warning ?? NOT_SELECTABLE_TEXT}
    </p>
  );
}
